import React from 'react';
import {GlobalContext} from "../../GlobalContext";
import {Button, Card, Col, Container, Row} from "react-bootstrap";
import {Link} from "react-router-dom";
import MarketingQueryResults from "./MarketingQueryResults";
import {Glyph} from "../common/Glyph";
import MarketingQueryConfigModal from "./MarketingQueryConfigModal";
import MarketingQueryColumnsModal from "./MarketingQueryColumnsModal";
import Cookies from "universal-cookie";

export const operators = {
    equal: {key: "equal", label: "Equals"},
    notEqual: {key: "notEqual", label: "Does Not Equal"},
    in: {key: "in", label: "Any Of"},
    notIn: {key: "notIn", label: "None Of"},
    gt: {key: "gt", label: ">"},
    ge: {key: "ge", label: ">="},
    lt: {key: "lt", label: "<"},
    le: {key: "le", label: "<="},
    startsWith: {key: "startsWith", label: "Starts With"},
    contains: {key: "contains", label: "Contains"},
    isTrue: {key: "isTrue", label: "Is True"},
    isFalse: {key: "isFalse", label: "Is False"},
}

export const types = {
    string: {name: "string", operators: [operators.equal, operators.notEqual, operators.in, operators.notIn, operators.gt, operators.ge, operators.lt, operators.le, operators.startsWith, operators.contains]},
    boolean: {name: "boolean", operators: [operators.isTrue, operators.isFalse]},
    int: {name: "int", operators: [operators.equal, operators.notEqual, operators.gt, operators.ge, operators.lt, operators.le]},
    lookup: {name: "lookup", operators: [operators.equal, operators.notEqual, operators.in, operators.notIn]},
    enum: {name: "enum", operators: [operators.equal, operators.notEqual, operators.in, operators.notIn]},
    date: {name: "date", operators: [operators.equal, operators.notEqual, operators.gt, operators.ge, operators.lt, operators.le]},
    datetime: {name: "datetime", operators: [operators.equal, operators.notEqual, operators.gt, operators.ge, operators.lt, operators.le]},
}

export const cols = {
    groupName: {label: "Practice Name", key: "groupName", type: types.string},
    providerFirst: {label: "First Name", key: "providerFirst", type: types.string},
    providerLast: {label: "Last Name", key: "providerLast", type: types.string},
    titleCode: {label: "Credentials", key: "titleCode", type: types.lookup, lookupTable: "provider_title", labelKey: "title", valueKey: "titleCode"},
    active: {label: "Active", key: "active", type: types.boolean},
    providerCount: {label: "# Providers", key: "providerCount", type: types.int},
    midlevelCount: {label: "# Midlevels", key: "midlevelCount", type: types.int},
    locationCount: {label: "# Locations", key: "locationCount", type: types.int},
    specialty: {label: "Specialty", key: "specialty", type: types.lookup, lookupTable: "specialty", filter: "active=1", labelKey: "specialty", valueKey: "specialtyCode"},
    groupType: {label: "Practice Type", key: "groupType", type: types.lookup, lookupTable: "group_type", labelKey: "name", valueKey: "groupTypeId"},
    rank: {label: "Rank", key: "rank", type: types.enum, values: ["A", "B", "C"]},
    refIn30d: {label: "Ref 30d", key: "refIn30d", type: types.int},
    refInPrev30d: {label: "Ref Prev 30d", key: "refInPrev30d", type: types.int},
    refIn1moDelta: {label: "Ref 30d Change", key: "refIn1moDelta", type: types.int},
    refIn6moAvg: {label: "Ref 6mo Avg", key: "refIn6moAvg", type: types.int},
    refIn12moAvg: {label: "Ref 12mo Avg", key: "refIn12moAvg", type: types.int},
    refIn24moAvg: {label: "Ref 24mo Avg", key: "refIn24moAvg", type: types.int},
    lastVisit: {label: "Last Visit", key: "lastVisit", type: types.date},
    zip: {label: "Zip", key: "zip", type: types.string}
};

const COOKIE_NAME = "marketingQueryConfig";

export default class MarketingQueryView extends React.Component {
    static contextType = GlobalContext;

    constructor(props) {
        super(props);
        const cookies = new Cookies();
        let conf = cookies.get("marketingQueryConfig");

        this.state = {
            resultset: null,
            config: conf ? this.expandConfig(conf) : this.defaultConfig(),
            pageSize: 50,
            pageNum: 0,
            showConfig: false,
            showColsModal: false,
            showResultsetsModal: false
        }
    }

    defaultConfig = () => {
        return {
            entityType: "Practice",
            columns: [cols.groupName, cols.active, cols.groupType, cols.specialty, cols.refIn6moAvg, cols.refIn30d],
            orders: [{col: cols.groupName, desc: false}],
            filters: [{col: cols.active, operator: operators.isTrue}],
            rowLimit: 500
        }
    }

    pollResultset = () => {
        let rs = this.state.resultset;
        let conf = this.shrinkConfig();
        let ordersParam = "";
        if (conf.orders && conf.orders.length > 0) {
            ordersParam = encodeURIComponent(JSON.stringify(conf.orders));
        }
        if (rs && rs.status === "Pending") {
            this.context.apiRequest("GET", `/marketingQuery/resultset/${rs.id}${ordersParam ? `?orders=${ordersParam}` : ""}`)
                .then(resp => {
                    if (resp && resp.data) {
                        this.setState({resultset: resp.data, pageNum: 0}, () => {
                            let rs = this.state.resultset;
                            if (rs.status !== "Complete") {
                                setTimeout(this.pollResultset, 1000);
                            }
                        });
                    }
                });
        }
    }

    goToPage = (pageNum) => {
        if (this.state.resultset) {
            let rs = this.state.resultset;
            let conf = this.shrinkConfig();
            let ordersParam = "";
            if (conf.orders && conf.orders.length > 0) {
                ordersParam = encodeURIComponent(JSON.stringify(conf.orders));
            }
            this.context.apiRequest("GET", `/marketingQuery/resultset/${rs.id}${ordersParam ? `?orders=${ordersParam}` : ""}&pageNum=${pageNum}&pageSize=${this.state.pageSize}`)
                .then(resp => {
                    if (resp && resp.data) {
                        this.setState({resultset: resp.data, pageNum}, () => {
                            let rs = this.state.resultset;
                            if (rs.status !== "Complete") {
                                setTimeout(this.pollResultset, 1000);
                            }
                        });
                    }
                });
        }
    }

    shrinkConfig = () => {
        let conf = {...this.state.config};
        conf.columns = conf.columns.map(c => ({key: c.key, label: c.label}));
        conf.orders = conf.orders.map(o => ({key: o.col.key, desc: o.desc}));
        conf.filters = conf.filters.map(f => ({key: f.col.key, operator: f.operator.key, value: f.value}));
        return conf;
    }

    expandConfig = (conf) => {
        conf.columns = conf.columns ? conf.columns.map(c => cols[c.key]) : [];
        conf.orders = conf.orders ? conf.orders.map(o => ({col: cols[o.key], desc: o.desc})) : [];
        conf.filters = conf.filters ? conf.filters.map(f => ({col: cols[f.key], operator: operators[f.operator], value: f.value})) : [];
        return conf;
    }

    executeQuery = () => {
        this.context.apiRequest("POST", "/marketingQuery/execute", this.shrinkConfig())
            .then(resp => {
                if (resp && resp.data) {
                    this.setState({resultset: resp.data}, () => {
                        let rs = this.state.resultset;
                        if (rs.status !== "Complete") {
                            setTimeout(this.pollResultset, 1000);
                        }
                    });
                }
            })
    }

    exportResults = () => {
        if (this.state.resultset) {
            let fname = null;
            this.context.basicApiRequest("POST", `/marketingQuery/resultset/${this.state.resultset.id}/export`, this.shrinkConfig())
                .then(res => {
                    let disp = res.headers.get("Content-Disposition");
                    if (disp) {
                        let arr = /.*\sfilename=\"(.+)\"/.exec(disp);
                        if (arr && arr.length > 1) fname = arr[1];
                    }
                    return res.blob();
                })
                .then(blob => {
                    // Create a temporary URL for the Blob
                    const url = URL.createObjectURL(blob);

                    // Create a link and set its href to the temporary URL
                    const link = document.createElement('a');
                    link.href = url;

                    // Set the link attributes for downloading
                    link.setAttribute('download', fname || 'SMART Query Results.csv');

                    // Programmatically click the link to initiate the download
                    link.click();

                    // Clean up the temporary URL
                    URL.revokeObjectURL(url);
                }) // Get the response as a Blob
                .catch(error => console.error(error));
        }
    }

    describeConfig = () => {
        let config = this.state.config;
        return <ul>
            <li>Type: {config.entityType}</li>
            <li>Filters: {config.filters && config.filters.length > 0 ? config.filters.map(this.describeFilter).join(", ") : "None"}</li>
            <li>Order By: {config.orders && config.orders.length > 0 ? config.orders.map(this.describeOrder).join(", ") : "None"}</li>
            <li>Limit: {config.rowLimit} Rows</li>
        </ul>
    }

    describeFilter = (f) => {
        return `${f.col.label} ${f.operator.label} ${Array.isArray(f.value) ? f.value.join(', ') : f.value === undefined ? "" : f.value}`;
    }

    describeOrder = (o) => {
        return `${o.col.label}${o.desc ? " (desc)" : ""}`;
    }

    configUpdated = (newConfig) => {
        this.setState({config: newConfig, showConfig: false}, () => {
            const cookies = new Cookies();
            let confStr = JSON.stringify(this.shrinkConfig());
            cookies.set("marketingQueryConfig", confStr, {path: "/"});
        });
    }

    colsUpdated = (cols) => {
        let newConfig = {...this.state.config};
        newConfig.columns = cols;
        this.configUpdated(newConfig);
    }

    render() {
        return <Container fluid>

            <MarketingQueryConfigModal config={this.state.config} show={this.state.showConfig} onCancel={() => this.setState({showConfig: false})} onOk={this.configUpdated}/>
            <MarketingQueryColumnsModal selectedCols={this.state.config.columns} show={this.state.showColsModal} onCancel={() => this.setState({showColsModal: false})} onOk={this.colsUpdated}/>
            <Row>
                <Col md={8}>
                    <h4>Advanced Marketing Query</h4>
                </Col>
                <Col md={4} className={"text-right"}>
                    <Link to={"/marketing"}>Marketing Dashboard</Link>
                </Col>
            </Row>
            <Row className={"mt-3"}>
                <Col md={8}>
                    <Card style={{ width: '40rem' }}>
                        <Card.Body className={"p-2"}>
                            <Card.Title>
                                Current settings:
                                <div style={{float: "right", display: "inline-block"}}>
                                    <Button variant={"secondary"} onClick={() => this.setState({showConfig: true})}><Glyph name={"filter"}/></Button>
                                    <Button variant={"secondary"} className={"ml-2"} onClick={() => this.setState({showColsModal: true})}><Glyph name={"table"}/></Button>
                                </div>
                            </Card.Title>
                            <Card.Text as={"div"}>
                                {this.describeConfig()}
                            </Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4} className={"text-right"}>
                    <Button variant={"secondary"} onClick={this.executeQuery}>Execute</Button>
                </Col>
            </Row>
            <Row className={"mt-3"}>
                <MarketingQueryResults config={this.state.config} resultset={this.state.resultset} pageNum={this.state.pageNum} setPageNum={this.goToPage} exportResults={this.exportResults}/>
            </Row>
            <Row className={"mb-3"}>&nbsp;</Row>
        </Container>
    }
}